import type { IndividualPaymentParcelViewModel } from '@/shared/api/generated';

import { getContractStatusTone } from '../types/contractStatus';
import { getParcelYear } from './getParcelYear';

/** Demonstrativo de um ano, derivado das parcelas do plano. */
export interface ConsolidationSummary {
  /** Parcelas que vencem no ano, em ordem crescente de vencimento. */
  readonly rows: readonly IndividualPaymentParcelViewModel[];
  /** Quantidade de parcelas no ano. */
  readonly total: number;
  /** Parcelas do ano com status de tom `positive`. */
  readonly paid: number;
  /** Parcelas do ano com status de tom `helper`. */
  readonly open: number;
  /** Soma dos valores líquidos das parcelas do ano. */
  readonly expectedValue: number;
  /** Soma de `totalPaid` das parcelas do ano. */
  readonly paidValue: number;
  /** Soma dos valores líquidos das parcelas do ano em aberto. */
  readonly openValue: number;
  /** Soma dos descontos das parcelas do ano. */
  readonly discountValue: number;
  /** Quantidade de parcelas do ano com desconto maior que zero. */
  readonly discountedCount: number;
}

/** Demonstrativo vazio — usado quando não há ano escolhido. */
const EMPTY_SUMMARY: ConsolidationSummary = {
  rows: [],
  total: 0,
  paid: 0,
  open: 0,
  expectedValue: 0,
  paidValue: 0,
  openValue: 0,
  discountValue: 0,
  discountedCount: 0,
};

/**
 * Desconto de uma parcela: a diferença entre o valor nominal e o valor com
 * desconto, nunca negativa.
 *
 * O piso em zero existe porque `parcelWithDiscount` pode vir **acima** de
 * `total` quando o backend já embute acréscimo — nesse caso a parcela não tem
 * desconto, e um número negativo poluiria a soma do indicador.
 *
 * @param parcel - Parcela do plano.
 * @returns O desconto em reais, ou `0` quando não há.
 */
export function getParcelDiscount(parcel: IndividualPaymentParcelViewModel): number {
  if (parcel.parcelWithDiscount == null || parcel.total == null) return 0;
  return Math.max(0, parcel.total - parcel.parcelWithDiscount);
}

/**
 * Valor líquido de uma parcela: o valor com desconto, degradando para o nominal.
 *
 * @param parcel - Parcela do plano.
 * @returns O valor líquido em reais.
 */
export function getParcelNetValue(parcel: IndividualPaymentParcelViewModel): number {
  return parcel.parcelWithDiscount ?? parcel.total ?? 0;
}

/**
 * Consolida as parcelas de um plano dentro de um ano-calendário.
 *
 * O backend não oferece endpoint de consolidação — os quatro indicadores da
 * régua, as linhas da tabela e o rodapé saem todos daqui, sobre a coleção
 * completa de parcelas que `GET .../financial-people/{id}/parcels` já devolve.
 *
 * A classificação de paga e em aberto reusa `getContractStatusTone`, a mesma
 * função que decide o tom da `Tag` de cada linha: com duas regras diferentes, a
 * régua poderia discordar da tabela na mesma tela. Status que não casa com
 * nenhuma palavra-chave conhecida entra no total do ano, mas não é contado como
 * pago nem como em aberto — igual ao tom `default` da sua `Tag`.
 *
 * @param parcels - Todas as parcelas do plano, já normalizadas.
 * @param year - Ano-calendário do recorte; `null` devolve o demonstrativo vazio.
 * @returns Linhas do ano e os agregados que alimentam régua e rodapé.
 *
 * @example
 * ```ts
 * const { rows, expectedValue, paid } = deriveConsolidation(parcels, 2023);
 * ```
 */
export function deriveConsolidation(
  parcels: readonly IndividualPaymentParcelViewModel[],
  year: number | null,
): ConsolidationSummary {
  if (year === null) return EMPTY_SUMMARY;

  // Ordena pela string ISO, não por `Date`: a comparação lexicográfica de
  // `YYYY-MM-DD…` já é cronológica e não passa por fuso — mesma razão de
  // `getParcelYear`. `sort` sobre uma cópia porque `parcels` é readonly.
  const rows = [...parcels]
    .filter(parcel => getParcelYear(parcel.expirationDate) === year)
    .sort((a, b) => (a.expirationDate ?? '').localeCompare(b.expirationDate ?? ''));

  let paid = 0;
  let open = 0;
  let expectedValue = 0;
  let paidValue = 0;
  let openValue = 0;
  let discountValue = 0;
  let discountedCount = 0;

  for (const parcel of rows) {
    const tone = getContractStatusTone(parcel.statusText);
    const netValue = getParcelNetValue(parcel);
    const discount = getParcelDiscount(parcel);

    if (tone === 'positive') paid += 1;
    else if (tone === 'helper') {
      open += 1;
      openValue += netValue;
    }

    expectedValue += netValue;
    paidValue += parcel.totalPaid ?? 0;

    if (discount > 0) {
      discountValue += discount;
      discountedCount += 1;
    }
  }

  return {
    rows,
    total: rows.length,
    paid,
    open,
    expectedValue,
    paidValue,
    openValue,
    discountValue,
    discountedCount,
  };
}
